import { useMemo, useState, type JSX } from "react";
import { useSearchParams } from "react-router-dom";
import { CopyableText } from "@via/components/copyable-text/copyable-text.tsx";
import { ActionButton } from "@via/components/action-button/action-button.tsx";
import { GenericError } from "@via/components/error.tsx";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import { ERROR } from "@via/utils/constants.tsx";
import { formatDateString } from "@via/utils/helpers.tsx";
import { PersonalAccessToken, PersonalAccessTokenUsage } from "common";
import {
  usePersonalAccessTokens,
  useDeletePersonalAccessToken,
} from "@via/pages/account/profile/personal-access-tokens/personal-access-tokens.api";
import AccessScopeBadge from "@via/pages/account/profile/personal-access-tokens/components/access-scope-badge";
import { useMcpConnectorUrl } from "@via/pages/account/profile/connect-ai/connect-ai.api";
import {
  CONNECT_CLIENTS,
  resolveClientId,
} from "@via/pages/account/profile/connect-ai/connect-clients";

export default function ConnectAssistants(): JSX.Element {
  const mcpUrl = useMcpConnectorUrl();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = resolveClientId(searchParams.get("client"));
  const activeClient =
    CONNECT_CLIENTS.find(client => client.id === activeId) ??
    CONNECT_CLIENTS[0];

  const { data, isLoading, isError } = usePersonalAccessTokens(0, 50, [
    { id: "createdAt", desc: true },
  ]);
  const [connectionToRevoke, setConnectionToRevoke] =
    useState<PersonalAccessToken | null>(null);
  const { mutateAsync: deletePersonalAccessToken, isPending: isRevoking } =
    useDeletePersonalAccessToken();

  const connections = useMemo(
    () =>
      (data?.data ?? []).filter(
        credential => credential.usageType === PersonalAccessTokenUsage.MCP
      ),
    [data]
  );

  // Keep the choice in the URL so a docs link and a click land on the same page.
  const selectClient = (id: string): void =>
    setSearchParams({ client: id }, { replace: true });

  return (
    <div className="p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-full sm:rounded-lg space-y-8">
      <div className="via-page-heading-wrapper">
        <h2 className="via-h2">Connect an AI assistant</h2>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Connector URL
        </label>
        <CopyableText value={mcpUrl} className="[&>span]:max-w-[600px]" />
        <p className="mt-2 text-sm text-gray-600 max-w-3xl">
          The same address works for every assistant below.
        </p>
      </div>

      <div>
        <div
          role="tablist"
          aria-label="AI assistant"
          className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
          {CONNECT_CLIENTS.map(client => (
            <button
              key={client.id}
              type="button"
              role="tab"
              id={`connect-client-tab-${client.id}`}
              aria-selected={client.id === activeId}
              aria-controls={`connect-client-panel-${client.id}`}
              onClick={() => selectClient(client.id)}
              className={
                client.id === activeId
                  ? "rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white"
                  : "rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200"
              }>
              {client.label}
            </button>
          ))}
        </div>

        <div
          role="tabpanel"
          id={`connect-client-panel-${activeClient.id}`}
          aria-labelledby={`connect-client-tab-${activeClient.id}`}
          className="mt-4 space-y-4">
          <p className="text-sm text-gray-600 max-w-3xl">
            {activeClient.summary}
          </p>

          {activeClient.status === "unavailable" ? (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-4">
              <p className="text-sm text-amber-900 max-w-3xl">
                {activeClient.unavailableReason}
              </p>
            </div>
          ) : (
            activeClient.steps.map((step, index) => (
              <div key={index}>
                <p className="text-sm text-gray-600 mb-2">{step.description}</p>
                {step.command ? (
                  <CopyableText
                    value={step.command(mcpUrl)}
                    className="[&>span]:max-w-[600px]"
                  />
                ) : null}
              </div>
            ))
          )}
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-1">
          Active connections
        </h3>
        <p className="text-sm text-gray-600 mb-3 max-w-3xl">
          Connections are read only unless you chose Read and change when you
          approved them. A connection can never administer this installation.
        </p>
        {isError ? (
          <div className="via-generic-error">
            <GenericError message={ERROR} />
          </div>
        ) : isLoading ? (
          <p className="text-sm text-gray-500">Loading connections…</p>
        ) : connections.length === 0 ? (
          <p className="text-sm text-gray-500">No active connections yet.</p>
        ) : (
          <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
            {connections.map(credential => (
              <li
                key={credential.id}
                className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
                    {credential.name}
                    <AccessScopeBadge scope={credential.scope} />
                  </p>
                  <p className="text-xs text-gray-500">
                    Expires {formatDateString(credential.expiresAt)}
                  </p>
                </div>
                <ActionButton
                  id={`revoke-connection-${credential.id}`}
                  button="Revoke"
                  primary={false}
                  showIcon={false}
                  onClick={() => setConnectionToRevoke(credential)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      <DeleteModal
        item={connectionToRevoke}
        setItem={setConnectionToRevoke}
        deleteFunction={() => deletePersonalAccessToken(connectionToRevoke!.id)}
        isLoading={isRevoking}
        title="Revoke Connection"
        confirmText="Revoke"
        message={name => (
          <>
            Are you sure you want to revoke the connection{" "}
            <strong className="font-semibold">{name}</strong>? The assistant
            will lose access to Foundry Connect through this connection and will
            need to reconnect. This action cannot be undone.
          </>
        )}
      />
    </div>
  );
}
